<template>
  <tx-dialog
    v-model="visible" :title="isAssignDesignOptionAsImage ? t('assignFile.assignDesignImageAsArticleImage') : t('assignFile.confirmation', { articleNumber: localAssetModel?.ImageSet })"
    :width="articleImageSupportAttributes.length ? '800px' : '600px'" :height="articleImageSupportAttributes.length ? '600px' : '400px'" show-ok-cancel :cancel-state="loading ? 'disabled' : 'enabled'" :ok-state="loading ? 'loading' : v$.$invalid ? 'disabled' : 'enabled'"
    @click="doCancel()" @ok="doConfirm()"
  >
    <div v-if="loading" class="flex items-center justify-center h-full">
      <loader style="z-index: 1801;" />
      <div class="absolute top-0 bottom-0 left-0 right-0 w-full h-full bg-transparent opacity-50" />
    </div>
    <div v-else class="flex flex-col w-full h-full">
      <tx-alert :show="hasError" type="error" :text="errorMessage" dismissible />
      <div class="grow flex overflow-hidden">
        <div class="flex flex-col w-full max-h-full overflow-hidden pr-4">
          <div class="text-base">
            {{ t('assignFile.imageOrResource') }}
          </div>
          <div v-if="!isAssignDesignOptionAsImage" class="flex flex-col gap-2 mt-2">
            <label v-if="allowImage && !isImageBlocked">
              <input v-model="form.source" type="radio" value="image">
              <span class="ml-1">{{ t('assignFile.image') }}</span>
            </label>
            <label v-if="allowResource && imageResourceType">
              <input v-model="form.source" type="radio" value="resource">
              <span class="ml-1">{{ t('assignFile.resource') }}</span>
            </label>
            <label v-if="allowImage">
              <input v-model="form.source" type="radio" value="designOption">
              <span class="ml-1">{{ t('assignFile.designOption') }}</span>
            </label>
          </div>
          <div v-show="form.source === 'resource'" class="mt-4">
            <div class="w-full my-2">
              <tx-input
                v-model="form.resourceName" :label="t('createResourceForm.resourceName')"
                :errors="v$.resourceName?.$errors" required @blur="v$.resourceName?.$touch"
              />
            </div>
            <div class="w-full my-2">
              <tx-input
                v-model="form.resourceDescription" :label="t('createResourceForm.description')"
                :errors="v$.resourceDescription?.$errors" @blur="v$.resourceDescription?.$touch"
              />
            </div>
          </div>
          <div v-show="form.source === 'image' || form.source === 'assignDesignOptionsAsImage'" class="mt-4">
            <div v-if="configuredImagekeys.length" class="w-full my-2">
              <tx-select
                v-model="form.imageKey" :label="t('assignFile.selectImageKey')" required :data="configuredImagekeys"
              />
            </div>
            <div v-if="form.source !== 'assignDesignOptionsAsImage' && articleImageSupportAttributes.length" class="w-full mb-5 mt-4 flex-1 overflow-hidden">
              <div class="overflow-y-auto" :class="{ 'max-h-[250px]': configuredImagekeys.length, 'max-h-[320px]': !configuredImagekeys.length }">
                <div v-if="localArticle" class="grid grid-cols-2 gap-x-6 gap-y-4 pt-1">
                  <div v-for="attribute in articleImageSupportAttributes" :key="attribute.SystemName">
                    <attribute-editor
                      v-model="form[attribute.SystemName]"
                      :articles="[localArticle]"
                      :attribute="attribute"
                      :required="attribute.IsRequired"
                      :disabled="attribute.ReadOnly"
                      :show-checkbox="false"
                      :form="form"
                      :errors="v$[attribute.SystemName]?.$errors"
                      @blur="v$[attribute.SystemName]?.$touch"
                      @change="onArticleAttributesChanged(attribute.SystemName)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-show="form.source === 'assignDesignOptionsAsImage'" class="mt-4">
            <div class="w-full my-2">
              <tx-select
                v-model="form.articleNumber" :label="t('assignFile.selectArticleNumber')" required :data="articleNumberList"
              />
            </div>
          </div>
          <div v-show="form.source === 'designOption'" class="mt-4">
            <div class="flex flex-col gap-2 mt-2">
              <label>
                <input v-model="form.designOptionFor" type="radio" value="model">
                <span class="ml-1">{{ t('assignFile.assignAsModelDesignOption') }}</span>
              </label>
              <label>
                <input v-model="form.designOptionFor" type="radio" value="article">
                <span class="ml-1">{{ t('assignFile.assignAsArticleDesignOption') }}</span>
              </label>
            </div>
          </div>
        </div>
        <img :src="previewImg" class="mx-auto w-60 h-60">
      </div>
    </div>
  </tx-dialog>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { v4 as guid } from 'uuid'
import { computed, nextTick, reactive, ref } from 'vue'
import type { ValidationRuleWithParams } from '@vuelidate/core'
import useVuelidate from '@vuelidate/core'
import { createI18nMessage, helpers, maxLength, minLength, required } from '@vuelidate/validators'
import { assign, clone, cloneDeep } from 'lodash-es'
import TxDialog from '@/shared/components/TxDialog.vue'
import TxAlert from '@/shared/components/TxAlert.vue'
import TxInput from '@/shared/components/TxInput.vue'
import type MyArticle from '@/models/myArticle'
import type { CreateUpdateAssetModel } from '@/api/dune/model/assetModel'
import { convertDesignOptionAsset, createAsset } from '@/api/dune/asset'
import useErrorMessage from '@/shared/composables/errorMessage'
import { useUserStore } from '@/store/userData'
import { assignArticlesToResource, createResourceWithArticlesOptional } from '@/api/t1/resource'
import Resource from '@/models/resource'
import appConfig from '@/services/appConfig'
import type { CreateResourceWithArticlesOptionalModel } from '@/api/t1/model/resourceModel'
import auth from '@/services/auth'
import Net from '@/services/net'
import utils from '@/services/utils'
import type ResourceType from '@/models/resourceType'
import TxSelect from '@/shared/components/TxSelect.vue'
import { privileges } from '@/models/constants'
import type DuneAsset from '@/models/duneAsset'
import type Article from '@/models/article'
import { useArticleFormHelper } from '@/shared/composables/articleFormHelper'
import AttributeEditor from '@/shared/components/AttributeEditor.vue'
import { updateArticle } from '@/api/t1/article'
import { AttributeType } from '@/models/catalogAttribute'
import Loader from '@/shared/components/Loader.vue'

interface IAssignImageForm {
  source: 'image' | 'resource' | 'designOption' | 'assignDesignOptionsAsImage'
  resourceName: string
  resourceDescription: string
  imageKey: string
  articleNumber: string
  designOptionFor: 'article' | 'model'
}

interface IProps {
  allowImage?: boolean
  allowResource?: boolean
}

const props = withDefaults(defineProps<IProps>(), { allowImage: true, allowResource: true })

const emit = defineEmits<{
  (e: 'cancel'): void
  (e: 'confirm', article: MyArticle, source?: string, asset?: string, isArticleUpdated?: boolean): void
  (e: 'confirmDesignOptionImageAsArticleImage', articleToRemoveFrom: MyArticle, articleToAddOn: Article, assetId: string): void
}>()

const { t } = useI18n()
const userStore = useUserStore()
const { errorMessage, hasError } = useErrorMessage()
const withI18nMessage = createI18nMessage({ t })
const { prefillAttributeValue, validateAndRemoveInvalidAttributeValues, getIndexedRestrictedAttributesBasedOnArticlesMaxSateRank, getIndexedRestrictedAttributesBasedOnArticlesStateRank, updateLookupAttributeValues, updateCriteriaAttributeAllowedValues } = useArticleFormHelper()

const visible = ref(false)
const loading = ref(false)
const configuredImagekeys = userStore.activeCatalog?.Config.WhiteboardImagePlaceholderOptions && userStore.activeCatalog?.Config.WhiteboardImagePlaceholderOptions.keys.length ? userStore.activeCatalog?.Config.WhiteboardImagePlaceholderOptions.keys : []
const startingSortOrder = ref(0)
const isImageBlocked = ref(false)
const articleNumberList = ref<string[]>([])
let articles: Article[] = []
const assetToConvert = ref<DuneAsset>()
const isAssignDesignOptionAsImage = ref(false)
const articleImageSupportAttributes = ref<IMyAttribute[]>([])
let isArticleAttributesChanged: boolean = false
const initialFormState: IAssignImageForm = {
  source: 'image',
  resourceName: '',
  resourceDescription: '',
  imageKey: configuredImagekeys.length ? configuredImagekeys[0] : '',
  articleNumber: '',
  designOptionFor: 'model',
}

const form = reactive<IAssignImageForm>({ ...initialFormState })

const localArticle = ref<MyArticle>()
const localAssetModel = ref<CreateUpdateAssetModel>()
const imageResourceType = ref<ResourceType>()
const existingResourceList = ref<string[]>([])

const commonArticleModel = computed(() => {
  const result: Record<string, any> = {}
  Object.keys(localArticle).forEach((property) => {
    if (!result.hasOwnProperty(property)) {
      result[property] = localArticle[property]
    }
    // eslint-disable-next-line eqeqeq
    if (result[property] != localArticle[property]) {
      result[property] = null
    }
  })
  return result
})

const rules = computed(() => {
  const result: Record<string, any> = {}
  if (form.source === 'image' && articleImageSupportAttributes.value.length) {
    articleImageSupportAttributes.value.forEach((attribute) => {
      result[attribute.SystemName] = {}
      if (attribute.AttributeType !== AttributeType.Calc) {
        if (attribute.IsRequired) {
          result[attribute.SystemName].required = helpers.withMessage(t('validations.required', { property: attribute.DisplayName }), required)
        }
        else if (utils.isValidStringValue(attribute.ValidationExpression)) {
          let message = utils.isValidStringValue(attribute.ValidationMessage) ? attribute.ValidationMessage : t('validations.invalidValue')
          if (utils.isDefined(attribute.parsedValidationExpression) && attribute.parsedValidationExpression.length) {
            const validationRules: ValidationRuleWithParams[] = []
            attribute.parsedValidationExpression.forEach((itm) => {
              if (utils.isValidStringValue(itm.Message)) {
                message = itm.Message
              }
              const validateExpression = () => {
                let isValid = false
                if (form.hasOwnProperty(attribute.SystemName) && (form.hasOwnProperty(itm.SourceField) || commonArticleModel.value.hasOwnProperty(itm.SourceField))) {
                  let currentValue = form[attribute.SystemName]
                  let sourceFieldValue = form.hasOwnProperty(itm.SourceField) ? form[itm.SourceField] : commonArticleModel.value[itm.SourceField]
                  if (utils.isValidStringValue(currentValue) && utils.isValidStringValue(sourceFieldValue)) {
                    switch (attribute.AttributeType) {
                      case AttributeType.Date:
                      case AttributeType.DateOption:
                      case AttributeType.DateTime:
                        currentValue = new Date(currentValue).getTime()
                        sourceFieldValue = new Date(sourceFieldValue).getTime()
                        break
                      default:
                        currentValue = currentValue.toString().trim().toLowerCase()
                        sourceFieldValue = sourceFieldValue.toString().trim().toLowerCase()
                    }
                  }
                  isValid = utils.validateExpression(currentValue, sourceFieldValue, itm)
                }
                return isValid
              }
              validationRules.push(helpers.withMessage(message, validateExpression))
            })
            result[attribute.SystemName].validateExpression = validationRules
          }
          else {
            try {
              const pattern = helpers.regex(new RegExp(attribute.ValidationExpression))
              result[attribute.SystemName].pattern = helpers.withMessage(message, pattern)
            }
            catch (e) {
              console.warn(`${attribute.SystemName} validation expression is invalid`, e)
            }
          }
        }

        if (attribute.maxLength != null) {
          result[attribute.SystemName].maxLength = helpers.withMessage(t('validations.maxLength', { property: attribute.DisplayName, max: attribute.maxLength }), maxLength(attribute.maxLength))
        }
      }
    })
  }
  if (form.source === 'resource') {
    result.resourceName = { required: withI18nMessage(required), minLength: withI18nMessage(minLength(1)), maxLength: withI18nMessage(maxLength(250)) }
    result.resourceDescription = { maxLength: withI18nMessage(maxLength(2000)) }
    if (utils.isValidStringValue(result.resourceName)) {
      const isResourceNameExist1 = value => !existingResourceList.value.includes(value.toLowerCase())
      // eslint-disable-next-line dot-notation
      result.resourceName['isResourceNameExist'] = helpers.withMessage(t('savedViewDialog.validations.savedViewNameExist'), isResourceNameExist1)
    }
  }
  else if (form.source === 'assignDesignOptionsAsImage') {
    result.articleNumber = { required: withI18nMessage(required) }
  }

  return result
})
const v$ = useVuelidate(rules, form)

const previewImg = computed(() => {
  if (localAssetModel.value && localAssetModel.value.file) {
    return URL.createObjectURL(localAssetModel.value.file)
  }
  else if (assetToConvert.value) {
    return `${appConfig.AssetsUrl}/assets/content/${assetToConvert.value.StorageFile}?ContextKey=${encodeURIComponent(userStore.activeCatalog!.ContextKey)}&f=webp&w=250&h=250&trim=true`
  }
  return ''
})
const haveUpdatePrivilege = computed(() => userStore.userProfile.isValidPrivilege(privileges.article.update))

async function showDialog(article: MyArticle, assetModel: CreateUpdateAssetModel, assignDesignOptionsAsImage: boolean = false, assetConvertingToArticleImage?: DuneAsset) {
  reset()
  isAssignDesignOptionAsImage.value = assignDesignOptionsAsImage
  assetToConvert.value = assetConvertingToArticleImage
  if (userStore.activeCatalog) {
    if (assignDesignOptionsAsImage) {
      visible.value = true
      loading.value = true
      form.source = 'assignDesignOptionsAsImage'
      const modelArticles = await appConfig.DB?.getArticlesByCatalogCodeModelIds(userStore.activeCatalog, userStore.linkedCatalogDetails, [article.ModelId], undefined)
      if (modelArticles && modelArticles.length) {
        articles = modelArticles
        articleNumberList.value = modelArticles?.map(article => article.ArticleNumber) || []
        form.articleNumber = article.ArticleNumber
        loading.value = false
        localArticle.value = article
        localAssetModel.value = assetModel
      }
    }
    else {
      isImageBlocked.value = (!userStore.userProfile.isValidPrivilege(privileges.article.allowBlockedArticleImages) && article.BlockImage === 1)
      if (props.allowImage && !isImageBlocked.value) {
        form.source = 'image'
      }
      else if (props.allowResource) {
        form.source = 'resource'
      }
      visible.value = true
      localArticle.value = article
      localAssetModel.value = assetModel
      startingSortOrder.value = assetModel.SortOrder
      // ArticleImageSupportAttributes
      let localReadOnly = false
      if ((userStore.userProfile.AccountDetails.AccountTypeId !== 1 && userStore.userProfile.AccountDetails.AccountId !== userStore.activeCatalog!.AccountId)
        || (utils.isDefined(userStore.currentCustomer) && localArticle.value._IsNonSegmented)) {
        localReadOnly = true
      }
      if (userStore.activeCatalog?.Config.ArticleImageSupportAttributes && userStore.activeCatalog?.Config.ArticleImageSupportAttributes.length) {
        const indexedRestrictedAttributesBasedOnArticlesMaxSateRank = getIndexedRestrictedAttributesBasedOnArticlesMaxSateRank([article])
        const indexedRestrictedAttributesBasedOnArticlesStateRank = getIndexedRestrictedAttributesBasedOnArticlesStateRank([article])
        const articleImageSupportAttributesMap: Record<string, number> = {}
        articleImageSupportAttributes.value = []
        userStore.activeCatalog.Config.ArticleImageSupportAttributes.forEach((attributeSystemName) => {
          const attribute = clone(userStore.myAttributes![attributeSystemName])
          if (attribute && !articleImageSupportAttributesMap.hasOwnProperty(attributeSystemName)) {
            const isRequired = attribute.IsRequired
            const readOnly = localReadOnly || !haveUpdatePrivilege.value || (!isRequired && attribute.ReadOnly) || (attribute.IsModelLevel && indexedRestrictedAttributesBasedOnArticlesMaxSateRank.hasOwnProperty(attribute.SystemName)) || (!attribute.IsModelLevel && indexedRestrictedAttributesBasedOnArticlesStateRank.hasOwnProperty(attribute.SystemName))
            const configuredStateLockingExternalChangeManagementURL = attribute.SystemName !== 'ModelName' ? appConfig.stateLockingExternalChangeManagementURL : appConfig.stateLockingExternalChangeManagementURLForModelName
            const stateLockingExternalChangeManagementURL = configuredStateLockingExternalChangeManagementURL && configuredStateLockingExternalChangeManagementURL.toString().trim().length
              && ((attribute.IsModelLevel && indexedRestrictedAttributesBasedOnArticlesMaxSateRank.hasOwnProperty(attribute.SystemName)
              && indexedRestrictedAttributesBasedOnArticlesMaxSateRank[attribute.SystemName].AllowChangeRequest) || (!attribute.IsModelLevel && indexedRestrictedAttributesBasedOnArticlesStateRank.hasOwnProperty(attribute.SystemName)
              && indexedRestrictedAttributesBasedOnArticlesStateRank[attribute.SystemName].AllowChangeRequest))
              ? configuredStateLockingExternalChangeManagementURL
              : undefined
            const stateName = attribute.IsModelLevel ? article.ModelStateName : article.StateName
            const attributeNonEditableOnStateMessage = indexedRestrictedAttributesBasedOnArticlesMaxSateRank.hasOwnProperty(attribute.SystemName) || indexedRestrictedAttributesBasedOnArticlesStateRank.hasOwnProperty(attribute.SystemName) ? `${attribute.DisplayName} is non editable at state ${stateName}` : ''
            const attributeToInsert: IMyAttribute = Object.assign({}, attribute, { ReadOnly: readOnly, IsRequired: isRequired, ExternalChangeManagementURL: stateLockingExternalChangeManagementURL, attributeNonEditableOnStateMessage })
            articleImageSupportAttributesMap[attributeSystemName] = 1
            articleImageSupportAttributes.value.push(attributeToInsert)
          }
          if (article.hasOwnProperty(attributeSystemName)) {
            const attributeField = articleImageSupportAttributes.value.find(attribute => attribute.SystemName === attributeSystemName)
            if (attributeField) {
              form[attributeSystemName] = prefillAttributeValue(form, article[attributeSystemName], attributeField)
              if (attributeSystemName === 'ColorId') { // if primary color is assigned then disable the primary color field
                attributeField.ReadOnly = !!utils.isDefined(form[attributeSystemName])
              }
            }
            else {
              form[attributeSystemName] = article[attributeSystemName]
            }
          }
          const dataForValidation = Object.assign({}, article, form)
          validateAndRemoveInvalidAttributeValues(articleImageSupportAttributes, form, dataForValidation)
        })
      }
      if (appConfig.DB && userStore.activeCatalog && utils.isValidStringValue(userStore.activeCatalog?.Config.WhiteboardDefaultResourceType)) {
        loading.value = true
        appConfig.DB.resourceTypes.get({ Type: userStore.activeCatalog.Config.WhiteboardDefaultResourceType })
          .then(async (resourceType) => {
            imageResourceType.value = resourceType
            existingResourceList.value = []
            if (userStore.activeCatalog && appConfig.DB && imageResourceType.value) {
              const allResources = await appConfig.DB!.resources.where('[CatalogCode+ResourceTypeId]').equals([userStore.activeCatalog.CatalogCode, imageResourceType.value.Id]).toArray()
              existingResourceList.value = allResources.map(resource => resource.ResourceName.toString().toLowerCase())
            }
          })
          .finally(() => {
            loading.value = false
          })
      }
    }
  }
}

function doCancel() {
  visible.value = false
  emit('cancel')
}

function reset() {
  loading.value = false
  errorMessage.value = ''
  assign(form, cloneDeep(initialFormState))
}

function onArticleAttributesChanged(attributeSystemName: string) {
  // Check if the attribute has changed
  if (form[attributeSystemName] !== localArticle[attributeSystemName]) {
    isArticleAttributesChanged = true
  }
  nextTick(() => {
    updateCriteriaAttributeAllowedValues(articleImageSupportAttributes, form, form)
    updateLookupAttributeValues(articleImageSupportAttributes, form, form, [])
  })
}

async function doConfirm() {
  errorMessage.value = ''
  if (!(await v$.value.$validate())) {
    errorMessage.value = t('validations.formInvalid')
    return
  }
  if (form.source === 'assignDesignOptionsAsImage') {
    if (assetToConvert.value && form.articleNumber && localAssetModel.value) {
      loading.value = true
      const fileExtension = assetToConvert.value?.FileName.split('.').pop()

      const assignedToArticle: Article | undefined = articles.find(article => article.ArticleNumber === form.articleNumber)
      const requestObject = {
        ContextKey: userStore.activeCatalog?.ContextKey,
        ImageSet: form.articleNumber,
        FileName: `${form.articleNumber}_${form.imageKey}.${fileExtension}`,
        Key: form.imageKey,
        AssetId: assetToConvert.value.Id,
        SortOrder: startingSortOrder.value + configuredImagekeys.indexOf(form.imageKey),
      }

      convertDesignOptionAsset(requestObject).then(async (response) => {
        emit('confirmDesignOptionImageAsArticleImage', localArticle.value!, assignedToArticle!, response.data.Id)
        visible.value = false
      })
        .catch((e) => {
          console.error(e)
          errorMessage.value = t('general.unexpectedError')
        })
        .finally(() => {
          loading.value = false
        })
    }
  }

  if (userStore.activeCatalog && localAssetModel.value && localArticle.value) {
    if (localAssetModel.value.Key !== form.imageKey) {
      localAssetModel.value.Key = form.imageKey
      localAssetModel.value.SortOrder = startingSortOrder.value + configuredImagekeys.indexOf(form.imageKey)

      if (localAssetModel.value.file) {
        const fileExtension = localAssetModel.value.file.name.split('.').pop()
        const newFilename = `${localAssetModel.value.ImageSet}_${form.imageKey}.${fileExtension}`
        localAssetModel.value.file = new File([localAssetModel.value.file], newFilename, { type: localAssetModel.value.file.type })
      }
    }
    if (form.source === 'designOption') {
      localAssetModel.value.Key = 'Design Option'
      localAssetModel.value.SortOrder = 0
      localAssetModel.value.ImageSet = form.designOptionFor === 'model' ? `${localArticle.value.ModelNumber.toString()}|DO` : `${localArticle.value.ArticleNumber.toString()}|DO`
      if (localAssetModel.value.file) {
        localAssetModel.value.file = new File([localAssetModel.value.file], `${localAssetModel.value.ImageSet}-${guid()}.${localAssetModel.value.file.name.split('.').pop()}`, { type: localAssetModel.value.file.type })
      }
    }

    if (form.source === 'image' || form.source === 'designOption') {
      loading.value = true
      let isArticleUpdated = !isArticleAttributesChanged
      if (articleImageSupportAttributes.value.length && isArticleAttributesChanged) {
        // update the article first and then the image
        const requestObj: Record<string, any> = {}
        articleImageSupportAttributes.value.forEach((attribute) => {
          if (form.hasOwnProperty(attribute.SystemName) && form[attribute.SystemName] !== localArticle.value![attribute.SystemName]) {
            const attributeValue = utils.getArticleAttributeTypeSpecificValue(attribute, form[attribute.SystemName])
            localArticle.value![attribute.SystemName] = attributeValue
            requestObj[attribute.SystemName] = attributeValue
          }
        })
        requestObj.ArticleNumber = localArticle.value.ArticleNumber
        requestObj.ArticleName = localArticle.value.ArticleName || localArticle.value.ModelName
        requestObj.ModelNumber = localArticle.value.ModelNumber
        await updateArticle(userStore.activeCatalog.CatalogCode, localArticle.value.Id, requestObj, true)
          .then(() => {
            isArticleUpdated = true
          })
          .catch((error: any) => {
            errorMessage.value = t('general.unexpectedError')
            if (utils.isDefined(error.response) && utils.isDefined(error.response.data) && Array.isArray(error.response.data) && error.response.data.length) {
              errorMessage.value = error.response.data.map(e => e.ErrorMessage).join(', ')
            }
          })
      }
      if (articleImageSupportAttributes.value.length === 0 || isArticleUpdated) {
        const requestObject: any = { ...localAssetModel.value }
        if (assetToConvert.value) {
          requestObject.AssetId = assetToConvert.value.Id
          requestObject.FileName = `${localAssetModel.value.ImageSet}-${guid()}.${assetToConvert.value.FileName.split('.').pop()}`
        }
        const promise = assetToConvert.value && assetToConvert.value.Id ? convertDesignOptionAsset(requestObject) : createAsset(requestObject)
        promise
          .then(async (res) => {
            emit('confirm', localArticle.value!, form.source, res.data.Id, isArticleUpdated)
            visible.value = false
          })
          .catch((e) => {
            console.error(e)
            errorMessage.value = t('general.unexpectedError')
            if (e.response && e.response.data && e.response.data.errorCode) {
              if (e.response.data.errorCode === 220) {
                errorMessage.value = t('assignFile.invalidFileExtension')
              }
              else {
                errorMessage.value = e.response.data.details
              }
            }
          })
          .finally(() => {
            loading.value = false
          })
      }
    }
    else if (form.source === 'resource' && imageResourceType.value) {
      if (userStore.activeCatalog && localArticle.value) {
        const catalogCode = userStore.activeCatalog.CatalogCode
        loading.value = true
        try {
          const existingResource = await appConfig.DB!.resources.where({ CatalogCode: catalogCode, ResourceName: form.resourceName, ResourceTypeId: imageResourceType.value.Id }).first()
          if (existingResource) {
            if (!existingResource.Articles.includes(localArticle.value.Id)) {
              existingResource.Articles.push(localArticle.value.Id)
              assignArticlesToResource(catalogCode, existingResource.ResourceId, existingResource.Articles)
                .then(async () => {
                  await appConfig.DB!.resources.put(existingResource)
                  emit('confirm', localArticle.value!)
                  visible.value = false
                })
                .catch((e) => {
                  console.error(e)
                  if (e.response?.data) {
                    if (e.response && e.response.data && e.response.data.errorCode) {
                      if (e.response.data.errorCode === 220) {
                        errorMessage.value = t('assignFile.invalidFileExtension')
                      }
                      else {
                        errorMessage.value = e.response.data.details
                      }
                    }
                  }
                  else {
                    errorMessage.value = t('general.unexpectedError')
                  }
                })
                .finally(() => {
                  loading.value = false
                })
            }
            else {
              visible.value = false
              loading.value = false
            }
          }
          else {
            const payload: CreateResourceWithArticlesOptionalModel = {
              TypeId: imageResourceType.value.Id,
              ResourceName: form.resourceName,
              Description: form.resourceDescription,
              FileId: await utils.uploadFlowFile(`${Net.t1.defaults.baseURL}/UploadFileChunk`, auth.getLocalToken() || '', localAssetModel.value.file!),
              IconFileId: null,
              Category: 'Support Imagery',
              Cascading: 0,
              SortOrder: 0,
              ArticleIds: [localArticle.value.Id],
            }

            createResourceWithArticlesOptional(catalogCode, payload)
              .then(async (res) => {
                const newResource = new Resource(catalogCode, res.data)
                await appConfig.DB!.resources.add(newResource)
                emit('confirm', localArticle.value!)
                visible.value = false
              })
              .catch((e) => {
                console.error(e)
                errorMessage.value = t('general.unexpectedError')
                if (e.response && e.response.data && e.response.data.errorCode) {
                  if (e.response.data.errorCode === 220) {
                    errorMessage.value = t('assignFile.invalidFileExtension')
                  }
                  else {
                    errorMessage.value = e.response.data.details
                  }
                }
              })
              .finally(() => {
                loading.value = false
              })
          }
        }
        catch (e: any) {
          errorMessage.value = t('general.unexpectedError')
          if (e && e.message) {
            try {
              const errorData = JSON.parse(e.message)
              errorMessage.value = errorData.Message
            }
            catch (er) {
              console.log(er)
            }
          }
          loading.value = false
        }
      }
    }
  }
}

defineExpose({
  showDialog,
})
</script>
